import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useAuth } from "providers/auth";
import { OriPersonaCard } from "components/ori";
import { SelectInputOption } from "apps/user/learner/api/types";
import { useTranslation } from "lib/i18n/use-translation";
import { trackEvent } from "utility/mixpanel";
import { getOnboardingData, setOnboardingData } from "./use-onboarding-state";
import { OnboardingLoader } from "./onboarding-loader";
import Logo from "Assets/images/logo.svg";
import styles from "../ori-profile.module.scss";

const MIN_DOB_AGE = 18;
const MAX_DOB_AGE = 120;

const shiftYears = (date: Date, years: number) => {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() - years);
  return next;
};

const toIsoDate = (date: Date) => date.toISOString().split("T")[0];

const isAtLeastAge = (iso: string, minAge: number) => {
  if (!iso) return true;
  const dob = new Date(iso);
  if (Number.isNaN(dob.getTime())) return false;
  return dob.getTime() <= shiftYears(new Date(), minAge).getTime();
};

type FieldErrors = {
  firstName?: string;
  lastName?: string;
  gender?: string;
  birthday?: string;
};

export const Profile = () => {
  const { learner, loading } = useAuth();
  const profileData = learner?.onboarding?.inputs.find(
    (item) => item.id === "woneProfile",
  );

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [selectGender, setSelectGender] = useState<string>("");
  const [birthdayDate, setBirthdayDate] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const navigate = useNavigate();
  const { t } = useTranslation(["onboard", "common"]);

  const stored = getOnboardingData();

  useEffect(() => {
    if (!stored) return;
    if (stored.firstName) setFirstName(stored.firstName);
    if (stored.lastName) setLastName(stored.lastName);
    if (stored.gender) setSelectGender(stored.gender);
    if (stored.birthday) setBirthdayDate(stored.birthday);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    trackEvent("Onboarding Personal Details Viewed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requiredCheck = (id: string) =>
    profileData?.inputs.find((item: SelectInputOption) => item.id === id);

  const firstNameField = requiredCheck("firstName");
  const lastNameField = requiredCheck("lastName");
  const genderField = requiredCheck("gender");
  const birthdayField = requiredCheck("birthday");

  const handleSubmit = () => {
    const errors: FieldErrors = {};
    if (firstNameField?.required && firstName.trim().length === 0) {
      errors.firstName = t("onboard:profile.validation.firstNameRequired");
    }
    if (lastNameField?.required && lastName.trim().length === 0) {
      errors.lastName = t("onboard:profile.validation.lastNameRequired");
    }
    if (genderField?.required && selectGender.trim().length === 0) {
      errors.gender = t("onboard:profile.validation.genderRequired");
    }
    if (birthdayField?.required && birthdayDate.trim().length === 0) {
      errors.birthday = t("onboard:profile.validation.dobRequired");
    } else if (
      birthdayDate.trim().length > 0 &&
      !isAtLeastAge(birthdayDate, MIN_DOB_AGE)
    ) {
      errors.birthday = t("onboard:profile.validation.minAge");
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      trackEvent("Onboarding Personal Details Validation Failed", {
        "Missing First Name": Boolean(errors.firstName),
        "Missing Last Name": Boolean(errors.lastName),
      });
      return;
    }

    setFieldErrors({});
    trackEvent("Onboarding Personal Details Submitted", {
      "Has DOB": Boolean(birthdayDate),
      "Has Gender": Boolean(selectGender),
    });
    setOnboardingData({
      firstName,
      lastName,
      gender: selectGender,
      birthday: birthdayDate,
    });
    navigate("/user/onboard/calendar-invites");
  };

  if (loading || !profileData) {
    return <OnboardingLoader />;
  }

  const genderOptions: SelectInputOption[] =
    (genderField as any)?.options ?? [];

  return (
    <div className={styles["screen"]} data-testid="onboarding-personal-details">
      <div className={styles["header"]}>
        <div className={styles["logoWrapper"]}>
          <img src={Logo} alt="Wone" className={styles["logo"]} />
        </div>
      </div>

      <div className={styles["content"]}>
        <div className={styles["leftColumn"]}>
          <OriPersonaCard message={t("onboard:profile.oriMessage")} />
        </div>

        <div className={styles["rightColumn"]}>
          <div className={styles["fieldList"]}>
            <div className={styles["field"]}>
              <label className={styles["label"]} htmlFor="onboard-first-name">
                {t("onboard:profile.firstNameLabel")}
              </label>
              <input
                id="onboard-first-name"
                type="text"
                className={clsx(
                  styles["input"],
                  fieldErrors.firstName && styles["inputError"],
                )}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t("onboard:profile.firstNamePlaceholder")}
                autoComplete="given-name"
                data-testid="onboard-input-first-name"
              />
              {fieldErrors.firstName ? (
                <p className={styles["errorText"]}>{fieldErrors.firstName}</p>
              ) : null}
            </div>

            <div className={styles["field"]}>
              <label className={styles["label"]} htmlFor="onboard-last-name">
                {t("onboard:profile.lastNameLabel")}
              </label>
              <input
                id="onboard-last-name"
                type="text"
                className={clsx(
                  styles["input"],
                  fieldErrors.lastName && styles["inputError"],
                )}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t("onboard:profile.lastNamePlaceholder")}
                autoComplete="family-name"
                data-testid="onboard-input-last-name"
              />
              {fieldErrors.lastName ? (
                <p className={styles["errorText"]}>{fieldErrors.lastName}</p>
              ) : null}
            </div>

            <div className={styles["field"]}>
              <label className={styles["label"]} htmlFor="onboard-birthday">
                {t("onboard:profile.dobLabel")}
              </label>
              <input
                id="onboard-birthday"
                type="date"
                className={clsx(
                  styles["input"],
                  !birthdayDate && styles["emptyField"],
                  fieldErrors.birthday && styles["inputError"],
                )}
                value={birthdayDate}
                onChange={(e) => setBirthdayDate(e.target.value)}
                max={toIsoDate(shiftYears(new Date(), MIN_DOB_AGE))}
                min={toIsoDate(shiftYears(new Date(), MAX_DOB_AGE))}
                data-testid="onboard-input-birthday"
              />
              {fieldErrors.birthday ? (
                <p className={styles["errorText"]}>{fieldErrors.birthday}</p>
              ) : null}
            </div>

            <div className={styles["field"]}>
              <label className={styles["label"]} htmlFor="onboard-gender">
                {t("onboard:profile.genderLabel")}
              </label>
              <select
                id="onboard-gender"
                className={clsx(
                  styles["select"],
                  !selectGender && styles["emptyField"],
                  fieldErrors.gender && styles["inputError"],
                )}
                value={selectGender}
                onChange={(e) => setSelectGender(e.target.value)}
                data-testid="onboard-input-gender"
              >
                <option value="">
                  {t("onboard:profile.genderPlaceholder")}
                </option>
                {genderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label.charAt(0).toUpperCase() + option.label.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
              {fieldErrors.gender ? (
                <p className={styles["errorText"]}>{fieldErrors.gender}</p>
              ) : null}
            </div>
          </div>

          <button
            type="button"
            className={styles["continueButton"]}
            onClick={handleSubmit}
            data-testid="onboard-personal-details-continue"
          >
            {t("common:action.continue")}
          </button>
        </div>
      </div>
    </div>
  );
};
